import { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Package, ToggleLeft, ToggleRight } from 'lucide-react'
import { toast } from 'sonner'
import z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { FormError } from '../components/ui/FormError'
import { SearchFilter } from '../components/ui/SearchFilter'
import { DiscreteLoader } from '../components/ui/discrete-loader'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '../components/ui/dialog'

import { banksService, BankItem } from '../services/banks.service'
import { useSessionStore } from '../store/auth/auth.store'

const formSchema = z.object({
    name: z.string().min(1, 'El nombre es obligatorio').min(3, 'El nombre debe tener al menos 3 caracteres'),
    is_active: z.boolean(),
})

type FormValidate = z.infer<typeof formSchema>

export function Banks() {
    const [data, setData] = useState<BankItem[]>([])
    const [paginationMeta, setPaginationMeta] = useState<any>({ total: 0, from: 0, to: 0 })
    const [isLoading, setIsLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState('')
    const [refreshCount, setRefreshCount] = useState(0)

    const [showModal, setShowModal] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)

    const [currentPage, setCurrentPage] = useState(1)
    const itemsPerPage = 10
    const canAction = useSessionStore((s) => s.canAction)
    const canCreateBank = canAction('configuration.bank.create')
    const canEditBank = canAction('configuration.bank.edit')
    const canChangeBankStatus = canAction('configuration.bank.status')
    const canDeleteBank = canAction('configuration.bank.delete')

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors, isSubmitting }
    } = useForm<FormValidate>({
        mode: 'onChange',
        resolver: zodResolver(formSchema as any),
        defaultValues: { name: '', is_active: true }
    })

    const isActiveValue = watch('is_active')

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true)
            try {
                const response = await banksService.getItems({
                    search: searchQuery,
                    page: currentPage
                })

                const items = response.data?.data || response.data || []
                setData(items)
                setPaginationMeta(response.data || response.meta || response || { total: 0, from: 0, to: 0 })
            } catch (error) {
                toast.error('Error al cargar los datos')
            } finally {
                setIsLoading(false)
            }
        }
        fetchData()
    }, [refreshCount, searchQuery, currentPage])

    const paginatedData = data

    const openCreateModal = () => {
        setEditingId(null)
        reset({ name: '', is_active: true })
        setShowModal(true)
    }

    const openEditModal = (item: BankItem) => {
        setEditingId(item.id)
        reset({ name: item.name, is_active: item.is_active })
        setShowModal(true)
    }

    const onSubmit = async (formData: FormValidate) => {
        try {
            if (editingId) {
                await banksService.updateItem(editingId, formData)
                toast.success('Banco actualizado exitosamente')
            } else {
                await banksService.createItem(formData)
                toast.success('Banco creado exitosamente')
            }
            setShowModal(false)
            setRefreshCount(prev => prev + 1)
        } catch (error) {
            toast.error('Error al guardar el banco. Verifica que el código no esté duplicado.')
        }
    }

    const handleStatusChange = async (id: number) => {
        try {
            await banksService.toggleStatus(id)
            toast.success('Estado actualizado exitosamente')
            setRefreshCount(prev => prev + 1)
        } catch (error) {
            toast.error('Error al actualizar el estado')
        }
    }

    const executeDelete = async () => {
        if (!deleteConfirmId) return
        try {
            await banksService.deleteItem(deleteConfirmId)
            toast.success('Banco eliminado')
            setRefreshCount(prev => prev + 1)
        } catch (error) {
            toast.error('Error al eliminar')
        } finally {
            setDeleteConfirmId(null)
        }
    }

    return (
        <div className="space-y-6 p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-slate-900">Bancos</h1>
                    <p className="mt-1 text-sm text-slate-600">Administra los bancos del sistema para la trazabilidad de ventas.</p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="relative w-full sm:w-64">
                        <SearchFilter
                            placeholder="Buscar..."
                            onSearch={(text) => {
                                setSearchQuery(text)
                                setCurrentPage(1)
                            }}
                        />
                    </div>
                    {canCreateBank && (
                        <Button onClick={openCreateModal} className="bg-[#3B7D3B] hover:bg-[#2d6a2d]">
                            <Plus className="mr-2 h-4 w-4" /> Nuevo Banco
                        </Button>
                    )}
                </div>
            </div>

            {isLoading ? (
                <TableSkeleton
                    headers={['Nombre', 'Código', 'Estado', 'Acciones']}
                    columns={['text', 'text', 'badge', 'actions']}
                    rows={5}
                />
            ) : (
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full whitespace-nowrap">
                            <thead className="border-b border-slate-200 bg-slate-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Nombre</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Código</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Estado</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {paginatedData.length > 0 ? (
                                    paginatedData.map((item) => (
                                        <tr key={item.id} className="hover:bg-slate-50">
                                            <td className="px-6 py-4 font-medium text-slate-900">{item.name}</td>
                                            <td className="px-6 py-4 text-slate-700">
                                                <Badge variant="outline">{item.code}</Badge>
                                            </td>
                                            <td className="px-6 py-4">
                                                <Badge className={item.is_active ? 'bg-green-100 text-green-700 border-green-200' : 'bg-slate-100 text-slate-600 border-slate-200'}>
                                                    {item.is_active ? 'Activo' : 'Inactivo'}
                                                </Badge>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    {canChangeBankStatus && (
                                                        <button
                                                            onClick={() => handleStatusChange(item.id)}
                                                            title={item.is_active ? 'Desactivar' : 'Activar'}
                                                            className={`rounded p-1.5 transition-colors ${item.is_active
                                                                ? 'text-green-600 hover:bg-green-50'
                                                                : 'text-slate-400 hover:bg-slate-100'
                                                                }`}
                                                        >
                                                            {item.is_active ? (
                                                                <ToggleRight className="h-5 w-5" />
                                                            ) : (
                                                                <ToggleLeft className="h-5 w-5" />
                                                            )}
                                                        </button>
                                                    )}
                                                    {canEditBank && (
                                                        <button onClick={() => openEditModal(item)} className="rounded p-1 hover:bg-slate-100" title="Editar">
                                                            <Edit className="h-4 w-4 text-slate-600" />
                                                        </button>
                                                    )}
                                                    {canDeleteBank && (
                                                        <button onClick={() => setDeleteConfirmId(item.id)} className="rounded p-1 hover:bg-red-50" title="Eliminar">
                                                            <Trash2 className="h-4 w-4 text-red-500" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-12 text-center">
                                            <Package className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                                            <p className="text-slate-500">No se encontraron registros</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {paginationMeta.total > 0 && (
                        <GlobalPaginator
                            totalItems={paginationMeta.total}
                            itemsPerPage={itemsPerPage}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                            from={paginationMeta.from}
                            to={paginationMeta.to}
                        />
                    )}
                </div>
            )}

            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editingId ? 'Editar Banco' : 'Nuevo Banco'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Nombre del Banco <span className="text-red-500">*</span></label>
                            <Input {...register('name')} placeholder="Ej. Banco Azteca" className={errors.name ? 'border-red-500 ' : ''} />
                            {errors.name && <FormError message={errors.name.message} />}
                        </div>

                        <div className='flex items-center gap-2'>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={isActiveValue}
                                onClick={() => setValue('is_active', !isActiveValue)}
                                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${isActiveValue ? 'bg-green-500' : 'bg-slate-200'
                                    }`}
                            >
                                <span
                                    aria-hidden="true"
                                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isActiveValue ? 'translate-x-2' : '-translate-x-2'
                                        }`}
                                />
                            </button>
                            <label htmlFor="is_active" className="text-sm font-medium text-slate-700">
                                Activo
                            </label>
                        </div>

                        <DialogFooter className="mt-6">
                            <Button type="button" variant="outline" onClick={() => setShowModal(false)} disabled={isSubmitting}>
                                Cancelar
                            </Button>
                            <Button type="submit" disabled={isSubmitting} className="bg-[#3B7D3B] hover:bg-[#2d6a2d]">
                                {isSubmitting ? <DiscreteLoader /> : 'Guardar'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <ConfirmationModal
                isOpen={deleteConfirmId !== null}
                onClose={() => setDeleteConfirmId(null)}
                onConfirm={executeDelete}
                preset="delete"
                itemName="banco"
            />
        </div>
    )
}
